import { MenuItem, Skeleton, TextField } from "@mui/material";
import {
  configurationsListBrokerConfigurationsOptions,
  configurationsSetRfpConfigurationMutation,
  useHeyApi,
} from "@platform/api";
import { useNotification } from "@platform/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";

import { useRfpConfiguration } from "../../../../hooks/useRfpConfiguration";
import { extractErrorMessage } from "../../../../utils";

interface MpConfigSelectProps {
  /** Ecosystem RFP id — how mpx scopes the selection. */
  ecoRfpId: string;
  /** Numeric broker id; the configurations offered are the ones assigned to it. */
  brokerId: number | undefined;
}

/**
 * Picks the MP configuration an RFP is spread against. The choice drives which
 * benefit fields the side-by-side pages show and which template the export
 * renders with, so it is persisted per RFP rather than held in component state.
 */
export const MpConfigSelect = ({
  ecoRfpId,
  brokerId,
}: MpConfigSelectProps): React.JSX.Element | null => {
  const { centroPortalClient, invalidateQuery } = useHeyApi();
  const { showError } = useNotification();

  const { data: options, isLoading: isLoadingOptions } = useQuery({
    ...configurationsListBrokerConfigurationsOptions({
      path: { brokerId: brokerId as number },
      client: centroPortalClient,
    }),
    // Checked by type, not against undefined: brokerId comes from a loosely
    // typed cache entry, and a null would otherwise reach the generated client
    // as a path param.
    enabled: typeof brokerId === "number",
  });

  const {
    configuration: resolvedConfiguration,
    source,
    isLoading: isLoadingSelection,
  } = useRfpConfiguration(ecoRfpId);

  const {
    mutate: selectConfiguration,
    isPending: isSaving,
    isError: hasAutoSelectionFailed,
  } = useMutation({
    ...configurationsSetRfpConfigurationMutation({
      client: centroPortalClient,
    }),
    onSuccess: () => {
      // The selection changes which fields the side-by-side pages render, so
      // everything keyed on it is refetched rather than patched locally.
      void invalidateQuery({
        tags: ["configurations"],
        path: { rfpId: ecoRfpId },
      });
      // The Financials grid resolves the configuration server-side (its
      // dollar-savings setting reaches the client already applied), so its own
      // query has to be refetched too — nothing in its payload changes when the
      // configurations cache does.
      void invalidateQuery({
        tags: ["centro-portal", "financials-table"],
        path: { id: ecoRfpId },
      });
    },
    onError: (err) => showError(extractErrorMessage(err)),
  });

  const brokerConfigurations = options?.configurations ?? [];

  // A broker with exactly one configuration has no choice to make: that one is
  // what the RFP should be spread against. Without a selection of its own the RFP
  // resolves to the published default instead, which is what the export and the
  // side-by-side pages would then use, so the selection is written rather than
  // only shown.
  const soleConfigurationId =
    brokerConfigurations.length === 1 ? brokerConfigurations[0].id : undefined;
  const autoSelected = React.useRef(false);

  React.useEffect(() => {
    // `source` reads "none" until the query settles, so an in-flight selection
    // would look like an absent one — and the broker configurations can arrive
    // first. Waiting for the query is what keeps an analyst's own selection from
    // being overwritten.
    if (
      isLoadingSelection ||
      !soleConfigurationId ||
      source === "rfp" ||
      autoSelected.current
    ) {
      return;
    }

    // The mutation's invalidation lands before the refetched `source` does, so
    // the write is latched rather than re-fired on the intervening renders.
    autoSelected.current = true;
    selectConfiguration({
      path: { rfpId: ecoRfpId },
      body: { configurationId: soleConfigurationId },
    });
  }, [
    isLoadingSelection,
    soleConfigurationId,
    source,
    ecoRfpId,
    selectConfiguration,
  ]);

  if (isLoadingOptions || isLoadingSelection) {
    return <Skeleton variant="rounded" width={220} height={32} />;
  }

  // What resolved is not always one of the broker's own: an RFP with no selection
  // falls back to the published default, which the broker need not be assigned
  // to. Offer it alongside so the picker shows what the export will actually use.
  const configurations =
    resolvedConfiguration &&
    !brokerConfigurations.some(
      (option) => option.id === resolvedConfiguration.id,
    )
      ? [...brokerConfigurations, resolvedConfiguration]
      : brokerConfigurations;

  // One configuration to offer is no choice at all, so the picker stays out of
  // the toolbar; the export drawer still names the configuration and its
  // template. The resolved default is not an option in that case — the broker's
  // own is being selected for the RFP above.
  //
  // A failed auto-selection is the exception: the RFP is still on whatever
  // resolved, and the picker is the only place an analyst can put it right.
  const isPickerRedundant = soleConfigurationId && !hasAutoSelectionFailed;

  if (configurations.length === 0 || isPickerRedundant) {
    return null;
  }

  return (
    <TextField
      select
      size="small"
      label="MP Config"
      value={resolvedConfiguration?.id ?? ""}
      disabled={isSaving}
      onChange={(event) =>
        selectConfiguration({
          path: { rfpId: ecoRfpId },
          body: { configurationId: event.target.value },
        })
      }
      sx={{ minWidth: 220, bgcolor: "background.paper" }}
    >
      {configurations.map((configuration) => (
        <MenuItem key={configuration.id} value={configuration.id}>
          {configuration.name}
          {configuration.isDefault ? " (default)" : ""}
        </MenuItem>
      ))}
    </TextField>
  );
};
